import React, { Fragment, useEffect, useState } from 'react';
import { fetchWrapper } from '../../../../../../_helpers';
import { toast } from 'react-toastify';
import { css } from '@emotion/react';
import ReactSelect from 'react-select';
import EmployeeCheckModal from '../EmployeeCheckModal';
import EmployeeCheckRow from '../EmployeeCheckRow';
import { VscLoading } from 'react-icons/vsc';
import Swal from 'sweetalert2';

function ZealEmployeePayroll({ employee, teamId, workLocations, previewFLSAOvertime }) {
    const employeeID = employee?.data?.basic?.employeeID || employee?.metadata?.employeeID;
    const companyID = employee?.data?.basic?.companyID;

    const [status, setStatus] = useState(null);

    const [employeeChecks, setEmployeeChecks] = useState([]);
    const [isBusy, setIsBusy] = useState(false);

    const [showEmployeeCheck, setShowEmployeeCheck] = useState({});

    async function fetchEmployeeChecks(companyID, employeeID, status) {
        try {
            setIsBusy(true);
            const response = await fetchWrapper.get(
                `/evv/payroll/zeal/employee/${companyID}/${employeeID}/checks?${status ? `status=${status}` : ''}${teamId ? `&team=${teamId}` : ''}`
            );
            setEmployeeChecks(response);
        } catch (error) {
            if (error.name === 'AbortError') return;
            toast.error("Couldn't fetch employee checks");
        } finally {
            setIsBusy(false);
        }
    }

    async function fetchEmployeePendingChecksWithFLSA(companyID, employeeID, status) {
        try {
            setIsBusy(true);
            const response = await fetchWrapper.get(
                `/evv/payroll/zeal/employee/${companyID}/${employeeID}/checks?${status ? `status=${status}` : ''}${teamId ? `&team=${teamId}` : ''}`
            );

            const flsaCheckIds = (response || [])
                .filter((check) => check?.flsa_ot_recommendation)
                .map((check) => check?.employeeCheckID);

            if (flsaCheckIds.length) {
                const jobResponse = await fetchWrapper.post(
                    `/evv/payroll/zeal/employee/${companyID}/preview/checks/ot`,
                    flsaCheckIds
                );

                const jobId = jobResponse?.data?.job_id;
                if (!jobId) {
                    setEmployeeChecks([]);
                    return toast.error('FLSA preview job failed. No valid JobId');
                }

                const jobStatusResponse = await getJobStatusWithRetries(jobId);

                if (jobStatusResponse?.status === 'complete') {
                    setEmployeeChecks(jobStatusResponse?.payload?.result || []);
                }
            } else {
                return toast.error('No pending FLSA checks found');
            }
        } catch (error) {
            if (error.name === 'AbortError') return;
            toast.error("Couldn't fetch employee checks");
        } finally {
            setIsBusy(false);
        }
    }

    async function getJobStatusWithRetries(jobId, maxRetries = 10, delay = 3000) {
        let response;
        let attempts = 0;
        while (attempts < maxRetries) {
            try {
                const jobStatusResponse = await fetchWrapper.get(`evv/payroll/zeal/preview?jobId=${jobId}`);

                if (jobStatusResponse?.status === 'complete') {
                    console.log('jobStatusResponse?.status', jobStatusResponse?.status);
                    response = jobStatusResponse;
                    break;
                }

                attempts++;
                if (attempts < maxRetries) {
                    await new Promise((resolve) => setTimeout(resolve, delay));
                }
            } catch (error) {
                if (error.name === 'AbortError') return;
                // toast.error('Error checking job status');
            }
        }
        if (response) {
            return response;
        } else {
            toast.error('Timeout: FLSA preview job did not complete in time');
        }
    }

    async function handleDeleteCheck(checkData) {
        if (isBusy) {
            return;
        }
        try {
            setIsBusy(true);
            if (!companyID) {
                throw new Error('Company ID is missing');
            }
            if (!checkData?.employeeCheckID) {
                throw new Error('Employee Check ID is missing');
            }
            const response = await Swal.fire(`You are deleting following check:
                 <br/> <p style="font-size: 14px;">${checkData.first_name} ${checkData.last_name} ${checkData.employeeCheckID}</p>
                 `);
            if (!response.value) {
                return;
            }
            await fetchWrapper.delete(
                `/evv/payroll/zeal/employee/${companyID}/${checkData.employeeCheckID}/checks${teamId ? `?team=${teamId}` : ''}`
            );
            toast.success(`Check deleted successfully!`);
            setEmployeeChecks((prev) =>
                prev.filter(({ employeeCheckID }) => employeeCheckID !== checkData?.employeeCheckID)
            );
        } catch (error) {
            toast.error(error.message || `Couldn't delete check!`);
        } finally {
            setIsBusy(false);
        }
    }

    async function handleApproveCheck(checkData) {
        if (isBusy) {
            return;
        }
        try {
            setIsBusy(true);
            if (!companyID) {
                throw new Error('Company ID is missing');
            }
            if (!checkData?.employeeCheckID) {
                throw new Error('Employee Check ID is missing');
            }
            const response = await Swal.fire(`You are approving following check:
                 <br/> <p style="font-size: 14px;">${checkData.first_name} ${checkData.last_name} ${checkData.employeeCheckID}</p>
                 `);
            if (!response.value) {
                return;
            }
            await fetchWrapper.patch(
                `/evv/payroll/zeal/employee/${companyID}/${checkData.employeeCheckID}/checks${teamId ? `?team=${teamId}` : ''}`
            );
            toast.success(`Check approved successfully!`);
            setEmployeeChecks((prev) =>
                prev.map((check) =>
                    check.employeeCheckID === checkData.employeeCheckID ? { ...check, approved: true } : check
                )
            );
        } catch (error) {
            toast.error(error.message || `Couldn't delete check!`);
        } finally {
            setIsBusy(false);
        }
    }

    useEffect(() => {
        if (showEmployeeCheck?.mode === 'delete' && showEmployeeCheck?.data?.employeeCheckID) {
            handleDeleteCheck(showEmployeeCheck.data);
        }
        if (showEmployeeCheck?.mode === 'approve' && showEmployeeCheck?.data?.employeeCheckID) {
            handleApproveCheck(showEmployeeCheck.data);
        }
    }, [showEmployeeCheck]);

    useEffect(() => {
        if (companyID && employeeID) {
            if (previewFLSAOvertime) {
                fetchEmployeePendingChecksWithFLSA(companyID, employeeID, 'pending');
            } else {
                fetchEmployeeChecks(companyID, employeeID, status);
            }
        }
    }, [status, companyID, employeeID, previewFLSAOvertime]);

    return (
        <div
            className="w-100 d-flex flex-column align-items-center"
            css={css`
                overflow-x: hidden;
                gap: 12px;
                padding: 12px;
            `}
        >
            <div className="d-flex flex-row w-100 align-items-end justify-content-between">
                <span>Employee Checks</span>
                {previewFLSAOvertime ? null : (
                    <div
                        css={css`
                            margin-left: auto;
                            display: flex;
                            flex-direction: row;
                            gap: 6px;
                            align-items: center;
                            justify-content: flex-end;
                        `}
                    >
                        <div>Check Status: </div>
                        <div
                            css={css`
                                width: 256px;
                            `}
                        >
                            <ReactSelect
                                options={[
                                    { label: 'Pending', value: 'pending' },
                                    { label: 'Pre-Processed', value: 'pre-processed' },
                                    { label: 'Processed', value: 'processed' },
                                ]}
                                value={
                                    status
                                        ? {
                                              label: {
                                                  pending: 'Pending',
                                                  'pre-processed': 'Pre-Processed',
                                                  processed: 'Processed',
                                              }[status],
                                              value: status,
                                          }
                                        : null
                                }
                                onChange={(option) => {
                                    setStatus(option?.value || '');
                                }}
                                isDisabled={isBusy}
                                isClearable
                            />
                        </div>
                    </div>
                )}
            </div>
            <Fragment>
                {isBusy ? (
                    <VscLoading
                        className="spin"
                        size="24px"
                        css={css`
                            overflow: hidden;
                        `}
                    />
                ) : (
                    <Fragment>
                        {employeeChecks?.length ? (
                            <div
                                css={css`
                                    width: 100%;
                                    display: flex;
                                    flex-direction: column;
                                    gap: 12px;
                                `}
                            >
                                {employeeChecks
                                    .sort((c1, c2) => new Date(c2?.check_date) - new Date(c1?.check_date))
                                    .map((employeeCheck, index) => (
                                        <EmployeeCheckRow
                                            key={index}
                                            employeeCheck={employeeCheck}
                                            setShowEmployeeCheck={(data, mode = 'view') => {
                                                setShowEmployeeCheck({ data, mode });
                                            }}
                                            companyID={companyID}
                                            teamId={teamId}
                                            fromPayroll={true}
                                        />
                                    ))}
                            </div>
                        ) : (
                            <div>No Checks Found</div>
                        )}
                    </Fragment>
                )}
            </Fragment>
            <EmployeeCheckModal
                show={
                    !!showEmployeeCheck?.data?.employeeCheckID &&
                    ['view', 'edit', 'clone'].includes(showEmployeeCheck?.mode)
                }
                handleClose={() => {
                    setShowEmployeeCheck({});
                }}
                companyID={companyID}
                employeeCheck={showEmployeeCheck?.data || {}}
                mode={showEmployeeCheck?.mode || 'view'}
                teamId={teamId}
                workLocations={workLocations}
            />
        </div>
    );
}

export default ZealEmployeePayroll;
